import React, { createContext, useState, useContext, useEffect, ReactNode } from "react";

// Define language type
type Language = {
  code: string;
  name: string;
  flag: string;
};

// Define languages object type
type LanguageMap = {
  [key: string]: Language;
};

// Define context value type
type LanguageContextType = {
  language: Language;
  changeLanguage: (code: string) => void;
  languages: LanguageMap;
};

// Available languages
export const LANGUAGES: LanguageMap = {
  EN: { code: "en", name: "English", flag: "🇬🇧" },
  NL: { code: "nl", name: "Nederlands", flag: "🇳🇱" }
};

// Create the context with initial undefined value
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Props for the provider component
interface LanguageProviderProps {
  children: ReactNode;
}

// The provider component
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language state from localStorage or browser settings
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem("preferredLanguage");
    
    if (savedLanguage && Object.values(LANGUAGES).some(lang => lang.code === savedLanguage)) {
      return Object.values(LANGUAGES).find(lang => lang.code === savedLanguage) || LANGUAGES.EN;
    }
    
    // Detect browser language - use just navigator.language which is standard
    const browserLang = navigator.language || 'en';
    const shortLang = browserLang.split('-')[0];
    
    // Check if browser language is supported
    const detectedLanguage = Object.values(LANGUAGES).find(
      lang => lang.code === shortLang
    );
    
    return detectedLanguage || LANGUAGES.EN;
  });

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem("preferredLanguage", currentLanguage.code);
  }, [currentLanguage]);

  // Function to change language
  const changeLanguage = (langCode: string) => {
    const newLanguage = Object.values(LANGUAGES).find(
      lang => lang.code === langCode
    );
    
    if (newLanguage) {
      setCurrentLanguage(newLanguage);
    }
  };

  // Value to be provided to consumers
  const contextValue: LanguageContextType = {
    language: currentLanguage,
    changeLanguage,
    languages: LANGUAGES
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};